import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import api from '../utils/api';
import { toast } from 'react-toastify';

// PAYMENT: no payment processor is wired up on the storefront yet. Checkout
// creates the order in a `pending` state and routes the customer to the
// order-success page. A high-risk payment processor will be integrated later —
// this submit handler is the seam to wire it in.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Active payment provider, decided by env:
//  - Wallet Plug (hosted redirect): create the order, get a checkout URL, leave
//    the SPA for Wallet Plug's page.
//  - NMI (Collect.js): route to the in-app /pay/:id card page.
//  - neither: keep today's behavior — order-success screen with follow-up
//    payment instructions.
const WALLETPLUG_ENABLED = String(process.env.REACT_APP_WALLETPLUG_ENABLED).toLowerCase() === 'true';
const NMI_ENABLED = Boolean(process.env.REACT_APP_NMI_TOKENIZATION_KEY);

export default function CheckoutPage() {
  const { items, total, retailTotal, retailUnit, clearCart, removeItem } = useCartCtx();
  const { isMember, user } = useAuthCtx();
  const { fmt } = useCurrency();
  const navigate = useNavigate();

  // Members pay VIP prices + ship free; everyone else pays retail. The backend
  // re-derives this authoritatively at checkout from the user's membership.
  const unitPrice = (i) => (isMember ? i.priceInCents : retailUnit(i));
  const subtotal  = isMember ? total : retailTotal;

  useEffect(() => {
    const stale = items.filter(i => !UUID_RE.test(String(i.variantId || '')));
    if (stale.length) {
      stale.forEach(i => removeItem(i.variantId));
      toast.warning(`Removed ${stale.length} outdated item(s) from your cart. Please re-add from the shop.`);
    }
  }, [items, removeItem]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart', { replace: true });
    }
  }, [items.length, navigate]);

  const [address, setAddress]           = useState({
    recipientName: '', line1: '', line2: '', city: '', state: '', postalCode: '', phone: '', country: 'US',
  });
  // Prefill the recipient with the account holder's name (editable — gifts /
  // different ship-to are common). Only sets it once, when empty.
  useEffect(() => {
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    if (fullName) setAddress(prev => prev.recipientName ? prev : { ...prev, recipientName: fullName });
  }, [user]);
  const [agreed, setAgreed] = useState(false);

  const [loading, setLoading] = useState(false);

  const shipping = isMember ? 0 : (subtotal >= 20000 ? 0 : 699); // VIP: free; else free over $200

  // ─── Coupon code ───────────────────────────────────────────────────
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon]           = useState(null);   // { code, description, discountInCents, freeShipping }
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const applyCouponCode = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const { data } = await api.post('/orders/validate-coupon', {
        code,
        subtotalInCents: subtotal,
        shippingInCents: shipping,
        scope: 'one_time',
      });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied!`);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.error || 'Could not apply coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const couponDiscount = coupon ? (coupon.freeShipping ? shipping : coupon.discountInCents) : 0;
  const effectiveShipping = coupon?.freeShipping ? 0 : shipping;
  const finalTotal = Math.max(effectiveShipping, subtotal + effectiveShipping - (coupon?.freeShipping ? 0 : couponDiscount));

  const proceedToPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/orders/checkout', {
        items: items.map(i => ({ variantId: i.variantId, quantity: i.quantity })),
        shippingAddress: {
          recipientName: (address.recipientName || '').trim(),
          line1:      (address.line1 || '').trim(),
          line2:      (address.line2 || '').trim(),
          city:       (address.city || '').trim(),
          state:      (address.state || '').trim(),
          postalCode: (address.postalCode || '').trim(),
          phone:      (address.phone || '').trim(),
          country:    (address.country || 'US').trim().toUpperCase().slice(0, 2),
        },
        couponCode: coupon?.code || undefined,
      });
      // Order is created as `pending`. Route to the active payment provider.
      const orderId = res.data.orderId;

      if (WALLETPLUG_ENABLED) {
        // Hosted redirect: ask the backend for Wallet Plug's checkout URL, then
        // leave the SPA. (If the order somehow already paid, skip to thank-you.)
        const { data } = await api.post(`/orders/${orderId}/walletplug/initiate`);
        if (data.alreadyPaid) {
          clearCart();
          navigate(`/thank-you/${orderId}`);
          return;
        }
        clearCart();
        window.location.href = data.paymentUrl;
        return;
      }

      // NMI in-app card page, or the no-processor fallback.
      clearCart();
      navigate(NMI_ENABLED ? `/pay/${orderId}` : `/order-success/${orderId}`);
      return;
    } catch (err) {
      const data = err.response?.data;
      const fieldMsg = Array.isArray(data?.fields) && data.fields.length
        ? data.fields.map(f => `${f.field}: ${f.message}`).join('; ')
        : null;
      toast.error(fieldMsg || data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-2">Checkout</h1>
        <p className="text-sm text-gray-500 mb-6">Prices in USD. Ships domestically.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Left: address + payment method form */}
          <div>
              <form onSubmit={proceedToPayment} className="card space-y-4">
                <h2 className="font-bold text-xl">Shipping Address</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-gray-400 font-normal">(recipient)</span></label>
                  <input
                    value={address.recipientName}
                    onChange={e => setAddress(prev => ({ ...prev, recipientName: e.target.value }))}
                    required className="input-field" maxLength={120}
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    value={address.line1}
                    onChange={e => setAddress(prev => ({ ...prev, line1: e.target.value }))}
                    required className="input-field" maxLength={200}
                    autoComplete="shipping address-line1" placeholder="123 Main St"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apt / Suite / Unit <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    value={address.line2}
                    onChange={e => setAddress(prev => ({ ...prev, line2: e.target.value }))}
                    className="input-field" maxLength={120}
                    autoComplete="shipping address-line2" placeholder="Apt 4B"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      value={address.city}
                      onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                      required className="input-field" maxLength={120}
                      autoComplete="shipping address-level2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                    <input
                      value={address.state}
                      onChange={e => setAddress(prev => ({ ...prev, state: e.target.value }))}
                      required className="input-field" maxLength={60}
                      autoComplete="shipping address-level1" placeholder="CA"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP / Postal Code</label>
                    <input
                      value={address.postalCode}
                      onChange={e => setAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                      required className="input-field" maxLength={20}
                      autoComplete="shipping postal-code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      value={address.country}
                      onChange={e => setAddress(prev => ({ ...prev, country: e.target.value }))}
                      className="input-field" maxLength={2}
                      autoComplete="shipping country" placeholder="US"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-gray-400 font-normal">(for delivery updates)</span></label>
                  <input
                    type="tel"
                    value={address.phone}
                    onChange={e => setAddress(prev => ({ ...prev, phone: e.target.value }))}
                    className="input-field" maxLength={40}
                    autoComplete="shipping tel" placeholder="(555) 555-5555"
                  />
                </div>

                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment</label>
                  <div className="border border-tea-200 bg-tea-50 rounded-lg p-3 dark:bg-slate-800 dark:border-slate-700">
                    <p className="text-sm text-gray-700">
                      Place your order now — our team will follow up with secure
                      payment instructions to complete it.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Online checkout is being onboarded and will be available here shortly.
                    </p>
                    <p className="flex items-center gap-1.5 text-xs font-medium text-tea-700 mt-2 dark:text-tea-300">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                      </svg>
                      256-bit SSL Secured Payment • PCI DSS Compliant Safe Check Out
                    </p>
                  </div>
                </div>

                <label className="flex gap-3 items-start text-sm text-gray-700 pt-2">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span>
                    I confirm I am a licensed researcher or laboratory provider and
                    acknowledge these products are <strong>for laboratory research
                    use only</strong> — not for human or veterinary consumption,
                    diagnosis, or treatment.
                  </span>
                </label>

                <button type="submit" disabled={loading || !agreed} className="btn-primary w-full py-3 disabled:opacity-50">
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>
              </form>
          </div>

          {/* Right: order summary */}
          <div className="card h-fit">
            <h3 className="font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm mb-4">
              {items.map(i => (
                <div key={i.variantId} className="flex justify-between">
                  <span className="text-gray-700">{i.productName} × {i.quantity}</span>
                  <span>{fmt(unitPrice(i) * i.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Non-member VIP upsell */}
            {!isMember && retailTotal - total > 0 && (
              <button
                type="button"
                onClick={() => navigate('/membership')}
                className="w-full text-left rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-3 hover:border-amber-300 transition-colors"
              >
                <p className="text-sm font-bold text-amber-800">★ Members pay {fmt(total)} — save {fmt(retailTotal - total)}</p>
                <p className="text-xs text-amber-600 mt-0.5">Unlock VIP pricing + free shipping from $5/mo →</p>
              </button>
            )}

            {/* Coupon code */}
            {(
              <div className="border-t pt-4 mb-3">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Coupon code</div>
                {coupon ? (
                  <div className="bg-tea-50 border border-tea-300 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono font-bold text-tea-700">{coupon.code}</div>
                      <div className="text-xs text-tea-700">
                        {coupon.freeShipping ? 'Free shipping' : `− ${fmt(coupon.discountInCents)} off`}
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input type="text" value={couponInput}
                        onChange={e => setCouponInput(e.target.value.toUpperCase())}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCouponCode(); } }}
                        placeholder="Enter code"
                        className="flex-1 text-sm font-mono uppercase tracking-wider border border-gray-300 rounded-lg px-3 py-2"
                        maxLength={50} />
                      <button type="button" onClick={applyCouponCode} disabled={validatingCoupon || !couponInput.trim()}
                        className="text-sm font-semibold px-4 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 disabled:opacity-50">
                        {validatingCoupon ? '…' : 'Apply'}
                      </button>
                    </div>
                    {couponError && <p className="text-xs text-red-500 mt-1">{couponError}</p>}
                  </>
                )}
              </div>
            )}

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal {isMember && <span className="text-[10px] font-bold uppercase text-amber-600">VIP</span>}</span><span>{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{effectiveShipping === 0 ? 'FREE' : fmt(effectiveShipping)}</span>
              </div>
              {coupon && !coupon.freeShipping && couponDiscount > 0 && (
                <div className="flex justify-between text-tea-700">
                  <span>Coupon ({coupon.code})</span>
                  <span>− {fmt(couponDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                <span>Total (USD)</span><span>{fmt(finalTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
